import React, { useState } from "react";
import { Button, DialogContent } from "@material-ui/core";
import Dialog from "src/components/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import { useSelector } from "react-redux";
import { selectUser } from "src/store/system/selector";
import { fetchStripeLink, updateUserApi } from "src/apiService";
import { buildZendeskLink } from "src/store/adminHtml/selector";
import { openZendeskWithSSO } from "src/utils/zendeskSso";
import { StatefulLoadableButton } from "src/components/LoadableButton";
import StripeDialog from "src/pages/RootDialogs/ProfileSettings/StripeDialog";
import Typography from "@material-ui/core/Typography";
import CostActionDialog from "src/components/CancellationSurvey/Dialog/CostActionDialog";
import DifficultyActionDialog from "src/components/CancellationSurvey/Dialog/DifficultyActionDialog";
import MissingFunctionalityDialog from "src/components/CancellationSurvey/Dialog/MissingFunctionalityDialog";
import UsingOtherProductDialog from "src/components/CancellationSurvey/Dialog/UsingOtherProductDialog";
import NotUsingItDialog from "src/components/CancellationSurvey/Dialog/NotUsingItDialog";
import SomethingElseDialog from "src/components/CancellationSurvey/Dialog/SomethingElseDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  surveyChoices: {
    display: "flex",
    justifyContent: "space-evenly",
    width: "100%",
    gap: theme.spacing(1),
  },
  surveyChoice: {
    flex: "1 1",
  },
  importantText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  calloutText: {
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  surveyResponseArea: {
    marginTop: theme.spacing(1),
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    borderWidth: "3px",
    borderRadius: "20px",
    padding: theme.spacing(2),
    paddingTop: theme.spacing(0),
    width: "100%",
  },
  headerText: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  doubleMarginRight: {
    marginRight: theme.spacing(2),
  },
  singlePaddingTop: {
    paddingTop: theme.spacing(1),
  },
  finalCancellationModalContent: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
  },
}));

const surveyOptions = [
  { text: "Cost", id: "costAction" },
  { text: "Difficulty Of Use", id: "difficultyAction" },
  { text: "Missing Functionality", id: "missingFunctionalityAction" },
  { text: "Using Other Product", id: "usingOtherProductAction" },
  { text: "Not Using It", id: "notUsingItAction" },
  { text: "...Something Else", id: "somethingElseAction" },
];

interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CancellationModal: ({
  isOpen,
  onClose,
}: CancellationModalProps) => React.JSX.Element | null = ({
  isOpen,
  onClose,
}) => {
  const classes = useStyles();
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [cancellationLink, setCancellationLink] = useState<
    string | undefined
  >();
  const [cancellationReason, setCancellationReason] = useState("");
  const user = useSelector(selectUser);

  if (!user) return null;

  const updateUserCancelInfo = async () => {
    await updateUserApi({
      cancellation: {
        cancellationReason: cancellationReason,
        cancellationDate: new Date().toISOString(),
        cancellationAction: selectedAction,
      },
    });
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        scroll="body"
        fullWidth
        maxWidth="lg"
      >
        <DialogContent className={classes.root}>
          <Typography variant="h3" className={classes.headerText}>
            Delete My Reseller Genie Account
          </Typography>
          <div>
            <Typography variant="body1">
              <Box component="span" fontWeight="fontWeightMedium">
                Cancelling your My Reseller Genie account will deactivate your
                login and delete all integrations at the end of the current
                billing cycle.
              </Box>{" "}
              Everything will stop functioning at that time, but your data and
              settings will be saved for 12 months - just in case you choose to
              re-activate your account during that time.
            </Typography>
          </div>
          <Typography variant="h6" className={classes.calloutText}>
            Please indicate the reason for deleting your account:
          </Typography>
          <div className={classes.surveyChoices}>
            {surveyOptions.map((option) => {
              if (selectedAction === option.id) {
                return (
                  <Button
                    className={classes.surveyChoice}
                    key={option.text}
                    size="large"
                    variant="outlined"
                    color="secondary"
                    onClick={() => setSelectedAction(option.id)} // Updates the state with the corresponding id
                  >
                    {option.text}
                  </Button>
                );
              } else {
                return (
                  <Button
                    className={classes.surveyChoice}
                    key={option.text}
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={() => setSelectedAction(option.id)} // Updates the state with the corresponding id
                  >
                    {option.text}
                  </Button>
                );
              }
            })}
          </div>
          {selectedAction !== null && (
            <Box
              display="block"
              id="surveyResponseArea"
              className={classes.surveyResponseArea}
            >
              {selectedAction === "costAction" && (
                <CostActionDialog
                  cancellationReason={cancellationReason}
                  setCancellationReason={setCancellationReason}
                />
              )}
              {selectedAction === "difficultyAction" && (
                <DifficultyActionDialog
                  cancellationReason={cancellationReason}
                  setCancellationReason={setCancellationReason}
                />
              )}
              {selectedAction === "missingFunctionalityAction" && (
                <MissingFunctionalityDialog
                  cancellationReason={cancellationReason}
                  setCancellationReason={setCancellationReason}
                />
              )}
              {selectedAction === "usingOtherProductAction" && (
                <UsingOtherProductDialog
                  cancellationReason={cancellationReason}
                  setCancellationReason={setCancellationReason}
                />
              )}
              {selectedAction === "notUsingItAction" && (
                <NotUsingItDialog
                  cancellationReason={cancellationReason}
                  setCancellationReason={setCancellationReason}
                />
              )}
              {selectedAction === "somethingElseAction" && (
                <SomethingElseDialog
                  cancellationReason={cancellationReason}
                  setCancellationReason={setCancellationReason}
                />
              )}
              <div className={classes.singlePaddingTop}>
                <Button
                  className={classes.doubleMarginRight}
                  color="secondary"
                  variant="contained"
                  onClick={() => setConfirmationOpen(true)}
                >
                  Cancel Subscription
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() =>
                    openZendeskWithSSO(
                      buildZendeskLink(
                        user,
                        "Cancellation Support",
                        "Please use this description box to outline the problems you are facing below:",
                        "cancellationReason:" + selectedAction,
                        "cancelation"
                      )
                    )
                  }
                >
                  Contact Support
                </Button>
              </div>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        scroll={"body"}
        fullWidth={true}
        maxWidth={"md"}
      >
        <DialogContent className={classes.finalCancellationModalContent}>
          <Typography variant="h4" className={classes.headerText}>
            Do you really want to delete your account?
          </Typography>
          <Typography variant="body1">
            Cancelling your account will delete all your integrations and
            prevent you from accessing data in My Reseller Genie. By cancelling
            your account, you acknowledge the following:
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                I understand that my subscription will be deleted and all the
                assets I’ve created -- including eBay integrations, Poshmark
                integration, Mercari integration, bank integrations, and credit
                card integrations -- will stop functioning and be deleted.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                I understand I can re-activate my account at any time over the
                next 12 months and recover my work.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                I understand that the price of My Reseller Genie is likely to
                increase in the future, and I may not be able to rejoin at my
                current, locked-in price.
              </Typography>
            </li>
          </ul>
          <div className={classes.singlePaddingTop}>
            <Button
              className={classes.doubleMarginRight}
              color="primary"
              variant="contained"
              onClick={() => {
                setConfirmationOpen(false);
                onClose();
              }}
            >
              Don't Cancel Subscription
            </Button>
            <StatefulLoadableButton
              variant="contained"
              color="secondary"
              onClick={async () => {
                const { data } = await fetchStripeLink();
                await updateUserCancelInfo();
                setCancellationLink(data.url);
              }}
              className={classes.doubleMarginRight}
            >
              Cancel Subscription
            </StatefulLoadableButton>
            <StripeDialog
              open={!!cancellationLink}
              handleClose={() => {
                setCancellationLink(undefined);
              }}
              link={cancellationLink}
            />
            <Button
              className={classes.doubleMarginRight}
              color="primary"
              variant="contained"
              onClick={() =>
                openZendeskWithSSO(
                  buildZendeskLink(
                    user,
                    "Cancellation Support",
                    "Please use this description box to outline the problems you are facing below:",
                    "cancellationReason:" + selectedAction,
                    "cancelation"
                  )
                )
              }
            >
              Contact Support
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CancellationModal;
